import { replaceSpecialCharacters, Status } from '@bcef/ia-shared';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { TerminationTypeFormName } from '@features/termination/consts/termination-type.const';
import {
  ChannelType,
  Civility,
  CivilityLabel,
  ClientType,
  ClientTypeLabel,
  ContractType,
} from '@features/termination/enums';
import { TerminationType } from '@features/termination/enums/termination-list.enum';
import {
  Config,
  ContentKeys,
  EmailRequest,
  LoadingStatus,
  PersonalInformation,
  TerminationInformation,
} from '@features/termination/interfaces';
import { Customer, Product } from '@features/termination/models';
import { encodeBase64 } from '@shared/utils/base64.util';
import { formatContent } from '@shared/utils/formatting.util';

import * as fromConfig from '../reducers/config/config.reducer';
import * as fromTermination from '../reducers/termination/termination.reducers';
import { selectConfigState } from './config/config.selectors';

export const selectTerminationState = createFeatureSelector<fromTermination.TerminationState>(
  fromTermination.terminationFeature.name,
);

export const selectTermination = createSelector(
  selectTerminationState,
  (state: fromTermination.TerminationState) => state,
);

export const selectCustomerInformationLoadingStatus = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): LoadingStatus =>
    state.customerInformationLoadingStatus,
);

export const selectCustomerInformationIsLoading = createSelector(
  selectCustomerInformationLoadingStatus,
  (status: LoadingStatus): boolean => status.isLoading,
);

export const selectProductListLoadingStatus = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): LoadingStatus => state?.productListLoadingStatus,
);

export const selectProductListStatus = createSelector(
  selectProductListLoadingStatus,
  (status: LoadingStatus) => {
    if (status?.isLoading) {
      return Status.PROCESSING;
    }
    if (status?.isLoaded) {
      return status.error ? Status.FAIL : Status.SUCCESS;
    }
    return Status.INITIAL;
  },
);

export const selectGroundsList = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): string[] => state.groundsList,
);

export const selectBankingProducts = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): Product[] =>
    [...state.bankingProducts].sort((a, b) => a.productName.localeCompare(b.productName)),
);

export const selectInsurances = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): Product[] =>
    [...state.insurances].sort((a, b) => a.productName.localeCompare(b.productName)),
);

export const selectStepInProcess = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): number => state.stepInProgress,
);

export const selectCustomer = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): Customer | undefined => state.customer,
);

export const selectCustomerIsPhysicalPerson = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): boolean => state.customer?.isPhysicalPerson || false,
);

export const selectTerminationInformation = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): TerminationInformation => state.terminationInformation,
);

export const selectTerminationInformationText = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): TerminationInformation => {
    const info = state.terminationInformation;
    const terminationTypeMap: Record<string, string> = {
      Résilier: 'screens.summary.section2.terminate',
      'Se rétracter': 'screens.summary.section2.cancel',
      Renoncer: 'screens.summary.section2.withdraw',
    };

    return {
      ...info,
      terminationType: terminationTypeMap[info.terminationType] || info.terminationType,
    };
  },
);

export const selectTelematicsId = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): string => state.customerTelematicsId,
);

export const selectPersonalInformation = createSelector(
  selectTermination,
  (state: fromTermination.TerminationState): PersonalInformation => state.personalInformation,
);

export const selectTerminationContractType = createSelector(
  selectTerminationInformation,
  (terminationInformation: TerminationInformation): string => terminationInformation.contractType,
);

export const selectTerminationType = createSelector(
  selectTerminationInformation,
  (terminationInformation: TerminationInformation): string =>
    terminationInformation.terminationType,
);

export const selectTerminationContract = createSelector(
  selectTerminationInformation,
  (terminationInformation: TerminationInformation): string => terminationInformation.contract,
);

export const selectSummaryNotice = createSelector(
  selectTerminationContractType,
  selectBankingProducts,
  selectInsurances,
  selectTerminationInformation,
  (
    contractType: string,
    bankingProducts: Product[],
    insurances: Product[],
    terminationInfo: TerminationInformation,
  ): string => {
    switch (contractType) {
      case ContractType.BANKING_PRODUCT:
        return (
          bankingProducts.find((product) => product.productName === terminationInfo.contract)
            ?.consequences || ''
        );

      case ContractType.INSURANCE:
        return (
          insurances.find((product) => product.productName === terminationInfo.contract)
            ?.consequences || ''
        );

      default:
        return '';
    }
  },
);

export const selectConfig = createSelector(
  selectConfigState,
  (state: fromConfig.ConfigState): Config => state.config,
);

export const selectGenderLabel = createSelector(
  selectPersonalInformation,
  (personalInformation: PersonalInformation): string => {
    if (personalInformation.gender === Civility.MADAM) {
      return CivilityLabel.MADAM;
    } else if (personalInformation.gender === Civility.MISTER) {
      return CivilityLabel.MISTER;
    } else {
      return '';
    }
  },
);

export const getClientTypeLabel = (clientType: string): string => {
  if (clientType === ClientType.LEGAL_PERSON) {
    return ClientTypeLabel.LEGAL_PERSON;
  }
  return ClientTypeLabel.PHYSICAL_PERSON;
};

export const selectTerminationInformationForEmailRequest = createSelector(
  selectTerminationInformation,
  selectTelematicsId,
  selectPersonalInformation,
  selectGenderLabel,
  selectConfig,
  selectCustomer,
  selectTerminationContractType,
  selectTerminationType,
  (
    terminationInformation: TerminationInformation,
    telematiqueId: string,
    personalInformation: PersonalInformation,
    genderLabel: string,
    config: Config,
    customer?: Customer,
    terminationContractType?: string,
    terminationType?: string,
  ): EmailRequest => {
    const contentObject: Record<string, string> = {
      [ContentKeys.date]: new Date().toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
      [ContentKeys.sender]: config.sender,
      [ContentKeys.formName]: (() => {
        const formNameMap: Map<string, string> = new Map([
          [
            `${ContractType.INSURANCE}|${TerminationType.TERMINATE}`,
            TerminationTypeFormName.Terminate,
          ],
          [`${ContractType.INSURANCE}|${TerminationType.CANCEL}`, TerminationTypeFormName.Cancel],
          [
            `${ContractType.BANKING_PRODUCT}|${TerminationType.TERMINATE}`,
            TerminationTypeFormName.Terminate,
          ],
          [
            `${ContractType.BANKING_PRODUCT}|${TerminationType.CANCEL}`,
            TerminationTypeFormName.Withdrawal,
          ],
        ]);

        const key = `${terminationContractType}|${terminationType}`;
        return formNameMap.get(key) || `${config.formname}\n`;
      })(),
      [ContentKeys.idendity]: getClientTypeLabel(personalInformation.identification),
      [ContentKeys.ikpi]: replaceSpecialCharacters(customer?.personIkpi || ''),
      [ContentKeys.telematicId]: replaceSpecialCharacters(telematiqueId),
      [ContentKeys.civility]: replaceSpecialCharacters(genderLabel),
      [ContentKeys.lastName]:
        personalInformation.identification === ClientType.PHYSICAL_PERSON
          ? replaceSpecialCharacters(personalInformation.fullName)
          : '',
      [ContentKeys.firstName]: replaceSpecialCharacters(personalInformation.firstName),
      [ContentKeys.companyName]:
        personalInformation.identification === ClientType.LEGAL_PERSON
          ? replaceSpecialCharacters(personalInformation.fullName || '')
          : '',
      [ContentKeys.requestorLastName]: replaceSpecialCharacters(
        personalInformation.applicantLastName,
      ),
      [ContentKeys.requestorFirstName]: replaceSpecialCharacters(
        personalInformation.applicantFirstName,
      ),
      [ContentKeys.birthDate]: replaceSpecialCharacters(
        customer
          ? personalInformation.dateOfBirth
          : new Date(personalInformation.dateOfBirth).toLocaleDateString('fr-FR'),
      ),
      [ContentKeys.birthCountry]: replaceSpecialCharacters(personalInformation.countryOfBirth),
      [ContentKeys.birthPlace]: replaceSpecialCharacters(personalInformation.communeOfBirth),
      [ContentKeys.canalType]:
        personalInformation.channelType === ChannelType.EMAIL ? 'Email' : 'Voie postale',
      [ContentKeys.clientMailAdress]: replaceSpecialCharacters(personalInformation.email),
      [ContentKeys.clientPostalAdress]: replaceSpecialCharacters(
        `${personalInformation.address} ${personalInformation.postalCode} ${personalInformation.city}`,
      ),
      [terminationType === TerminationType.TERMINATE
        ? ContentKeys.terminationType
        : ContentKeys.contractType]: replaceSpecialCharacters(
        terminationInformation.terminationType,
      ),
      [terminationType === TerminationType.TERMINATE
        ? ContentKeys.product
        : ContentKeys.productConcerned]: replaceSpecialCharacters(terminationInformation.contract),
      [ContentKeys.request]: replaceSpecialCharacters(terminationType || ''),
      [ContentKeys.contractName]: replaceSpecialCharacters(terminationInformation.contractName),
      [ContentKeys.contractNumber]: replaceSpecialCharacters(terminationInformation.contractNumber),
    };

    if (terminationType === TerminationType.TERMINATE) {
      contentObject[ContentKeys.desiredTerminationDate] = replaceSpecialCharacters(
        terminationInformation.contractType === ContractType.INSURANCE
          ? new Date(terminationInformation.terminationDate).toLocaleDateString('fr-FR')
          : terminationInformation.terminationDate,
      );
      contentObject[ContentKeys.terminationReason] = replaceSpecialCharacters(
        terminationInformation.terminationReason,
      );
      contentObject[ContentKeys.otherTerminationReasons] = replaceSpecialCharacters(
        terminationInformation.otherReason,
      );
    }
    return {
      recipient: config.recipient.map(encodeBase64),
      subject: encodeBase64(config.subject),
      content: encodeBase64(formatContent(contentObject)),
    };
  },
);
